/**
 * Case-study source of truth.
 *
 * Writing rules (plain English — for investors AND non-technical readers):
 *  • Lead with ONE big number (dollars saved, hours back, days earlier)
 *  • Challenge: what was going wrong, in 2–3 short sentences your parents would understand
 *  • Solution: what the team did with ARISSAI, no jargon
 *  • Results: 3 short bullets, each starting with a number
 *  • Quote: sounds like a real person, not a marketing brochure
 *
 * The same content is used by /app/backend/scripts/build_case_studies_pdf.py
 * to generate the 2-page shareable PDF at /ARISSAI_Case_Studies.pdf.
 * Field names (challenge, solution, results, quote) must stay stable — the
 * CaseStudies page AND the PDF script both read them.
 */
export const CASE_STUDIES = [
  {
    slug: "saas-release-risk-radar",
    published: true,
    title: "Software team saves 6 hours a week and catches 3× more problems",
    hero_takeaway: "From gut-feel decisions to a clear health score in under a minute.",
    headline_stat: { value: "6h", unit: "/week", caption: "Time saved every week" },
    secondary_stat: { value: "3×", caption: "More issues caught early" },
    industry: "Technology & Software",
    industrySlug: "technology_saas",
    accent: "#0020F5",
    customer: "Design partner · 32-person software team",
    period: "Q1 2026",
    challenge:
      "Every second Friday, the team lead wrote a long status email that nobody actually read. Almost every sprint, something obvious blew up in production — a missing test, a slow database, a person on holiday — and the whole team said the same thing: 'we should have seen this coming.'",
    solution:
      "Now, before every planning meeting, the team uploads their task list to ARISSAI. In under a minute it reads every task, gives each one a health score from 0 to 100, points out the risky ones, and writes a short summary the manager can share with leadership.",
    results: [
      "6 hours a week given back to the team lead",
      "3× more critical issues caught before the sprint (not after)",
      "The Friday email is now a 1-click PDF everyone actually reads",
    ],
    quote:
      "We used to argue about risk after things broke. Now ARISSAI shows us the risk before the sprint starts. Friday is a five-minute check instead of a two-hour writing job.",
    attribution: "Team lead · Anonymised design partner",
    investor_view:
      "Software team leads spend 4–8 hours a week writing status updates. ARISSAI does it in seconds. Over 250,000 team leads in North America and Europe fit this profile — most convert to the $79/mo Pro tier within a week.",
  },
  {
    slug: "enterprise-pmo-reporting-60",
    published: true,
    title: "Consulting team cuts weekly reporting from 2 days to 1 afternoon",
    hero_takeaway: "40 people were spending 16 hours a week on spreadsheets. ARISSAI now does it before Monday coffee.",
    headline_stat: { value: "60%", unit: "↓", caption: "Less time spent on reports" },
    secondary_stat: { value: "3 weeks", caption: "Earlier warning on problems" },
    industry: "Consulting Services",
    industrySlug: "consulting_pmo",
    accent: "#7C3AED",
    customer: "Pilot · 40-person delivery team",
    period: "Q1 2026",
    challenge:
      "Twelve project managers each used a slightly different Excel spreadsheet. Every week, the department head spent two full working days just lining up their columns so numbers matched. That left almost no time to actually think about the projects or write a clear update for senior leadership.",
    solution:
      "Every Monday morning, each project manager's spreadsheet is uploaded to ARISSAI. All twelve projects are then scored on the same 0–100 scale, and a clean two-page summary lands on the leadership team's desk automatically before 8am.",
    results: [
      "Weekly reporting dropped from 16 hours to about 6",
      "Leadership now sees updates every week, not once a month",
      "Serious problems are spotted about 3 weeks earlier than before",
    ],
    quote:
      "ARISSAI took our reporting from two full days down to one afternoon. The two-page brief is now the first thing our leadership reads every Monday.",
    attribution: "Department head · North America",
    investor_view:
      "Every consulting firm we've spoken with runs project reporting on spreadsheets. ARISSAI is the tool they can wrap into every client engagement. Typical customer value: 12–24× a monthly Pro seat.",
  },
  {
    slug: "finance-capex-assumption-audit",
    published: true,
    title: "Finance team spots $2.4M hidden risk before signing a $14M deal",
    hero_takeaway: "One 15-minute check paid for a full year of the platform.",
    headline_stat: { value: "$2.4M", caption: "Risk caught before signing" },
    secondary_stat: { value: "22h", caption: "Analyst hours saved" },
    industry: "Finance",
    industrySlug: "finance_fpa",
    accent: "#16A34A",
    customer: "Design partner · Mid-sized finance team",
    period: "Q2 2026",
    challenge:
      "The team was two days away from approving a $14 million investment. Nobody had double-checked the assumptions underneath it: supplier delivery dates, exchange rates, hiring plans, a new regulation on the horizon. Two months earlier, this exact type of oversight had already cost the company $700,000.",
    solution:
      "They uploaded the business case and the Excel model to ARISSAI. The platform pulled out 19 hidden assumptions, gave each one a confidence score, and flagged four as 'very risky if wrong' — together worth $2.4M in exposure. Leadership got a one-paragraph summary they could act on the same afternoon.",
    results: [
      "$2.4M of risk found in four unchecked assumptions",
      "Deal was restructured into staged payments instead of a lump sum",
      "22 analyst hours saved on this single approval",
    ],
    quote:
      "We were 48 hours away from signing off $14M when ARISSAI flagged four things we hadn't checked. That single afternoon paid for a whole year of the platform.",
    attribution: "Senior finance manager · Anonymised design partner",
    investor_view:
      "Finance teams sign off on billions of dollars every year based on assumptions nobody stress-tests. A $2.4M save on a $79/mo tool is the story we open every enterprise conversation with.",
  },
  {
    slug: "healthcare-ops-incident-radar",
    published: true,
    title: "Healthcare team spots a staffing shortfall 19 days sooner",
    hero_takeaway: "Six clinics rolling out a new system — one clear view every Monday.",
    headline_stat: { value: "19 days", caption: "Earlier warning" },
    secondary_stat: { value: "6 sites", caption: "Scored on the same scale" },
    industry: "Healthcare",
    industrySlug: "healthcare_ops",
    accent: "#00C7BE",
    customer: "Pilot · Six-clinic rollout",
    period: "Q2 2026",
    challenge:
      "A group of six outpatient clinics was rolling out a new patient booking system. More than 80 risks were tracked in a single messy spreadsheet that took 3 hours a week to update. Important things — like a clinic being short-staffed on go-live day — kept getting buried under small design bugs, because there was no way to compare severity across sites.",
    solution:
      "Every week, each clinic's spreadsheet is uploaded to ARISSAI. All risks are scored on the same 0–100 scale, root causes are auto-suggested, and a two-page brief gives leadership one clear view across all six sites.",
    results: [
      "A critical patient-facing risk was caught 19 days earlier than before",
      "All six clinics now use the same fair scoring system",
      "Weekly meeting prep dropped from 4 hours to about 45 minutes per site lead",
    ],
    quote:
      "Patient-facing risk used to hide in a spreadsheet column. Now ARISSAI ranks it, colours it, and tells me which three sites need my attention before Monday morning.",
    attribution: "Programme lead · Healthcare design partner",
    investor_view:
      "In healthcare, a missed risk means missed patient care. Every regional hospital group in North America is a 5–10 seat Enterprise account. Our Canadian privacy alignment (PIPEDA) is a natural moat.",
  },
];

export const PUBLISHED_CASE_STUDIES = CASE_STUDIES.filter((c) => c.published);
